import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AlertCircle, Lock, LogIn, Mail, ShieldCheck } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../ui';
import { LoginError } from '../auth/api';

// Operator login (Cortex-styled, DESIGN_LANGUAGE_SPEC §10 decorative background, §23 inputs,
// §25 error banner). Dark-luxury direction: layered radial glows + a faint grid behind a glass
// auth card. Semantic <form> with labelled inputs, a visible focus ring, an assertive error
// banner, and a loading button state. The password is only ever read into the request body.

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { signIn, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as LocationState | null)?.from?.pathname ?? '/clients';

  // Already signed in (e.g. navigated back to /login) — bounce to the app.
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn({ email, password });
      void navigate(from, { replace: true });
    } catch (cause: unknown) {
      const message =
        cause instanceof LoginError
          ? cause.message
          : 'Something went wrong. Please try again.';
      setError(message);
      setSubmitting(false);
    }
  };

  const inputClasses = `w-full rounded-xl border py-3 pl-11 pr-4 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
    isDark
      ? 'border-white/[0.06] bg-white/[0.04] text-white placeholder-white/30 focus:border-indigo-500 focus:bg-white/[0.06]'
      : 'border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:bg-white'
  }`;

  const iconClasses = `pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 ${
    isDark ? 'text-white/30' : 'text-slate-400'
  }`;

  return (
    <main
      className="theme-bg-primary relative flex min-h-screen items-center justify-center overflow-hidden p-6 transition-colors duration-300"
    >
      <style>{`
        @keyframes loginPulseGlow { 0%, 100% { opacity: 0.45; } 50% { opacity: 0.9; } }
        @keyframes loginRise { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Decorative background — grid + floating orbs (§10). */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          color: 'var(--theme-text-primary)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/4 top-1/4 h-80 w-80 -translate-x-1/2 rounded-full bg-indigo-500/30 blur-[120px]"
        style={{ animation: 'loginPulseGlow 5s ease-in-out infinite' }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-1/4 right-1/4 h-96 w-96 translate-x-1/2 rounded-full bg-purple-500/20 blur-[140px]"
        style={{ animation: 'loginPulseGlow 7s ease-in-out infinite 1.5s' }}
      />

      {/* Auth card */}
      <section
        className={`relative w-full max-w-md rounded-3xl border p-8 shadow-2xl backdrop-blur-xl ${
          isDark
            ? 'border-white/[0.1] bg-gradient-to-br from-white/[0.08] to-white/[0.02] shadow-black/50'
            : 'border-slate-200 bg-white/90 shadow-slate-200/60'
        }`}
        style={{ animation: 'loginRise 0.6s ease-out forwards' }}
        aria-labelledby="login-heading"
      >
        {/* Brand mark */}
        <div className="mb-8 flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25">
            <ShieldCheck className="h-6 w-6 text-white" aria-hidden="true" />
          </span>
          <span className="flex flex-col">
            <span
              className={`text-[10px] font-medium uppercase leading-none tracking-widest ${
                isDark ? 'text-white/40' : 'text-slate-400'
              }`}
            >
              DreamWalk
            </span>
            <span
              className={`-mt-px text-2xl font-bold leading-tight tracking-tight ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}
            >
              AI Proxy
            </span>
          </span>
        </div>

        <h1
          id="login-heading"
          className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          Operator sign in
        </h1>
        <p className={`mt-1.5 text-sm ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
          Access the tenancy, routing, and usage console.
        </p>

        {error && (
          <div
            role="alert"
            aria-live="assertive"
            className={`mt-6 flex items-center gap-3 rounded-xl p-3.5 ${
              isDark
                ? 'border border-rose-500/20 bg-rose-500/10'
                : 'border border-rose-200 bg-rose-50'
            }`}
          >
            <AlertCircle
              className="h-5 w-5 flex-shrink-0 text-rose-400"
              aria-hidden="true"
            />
            <p className={`text-sm ${isDark ? 'text-rose-300' : 'text-rose-700'}`}>
              {error}
            </p>
          </div>
        )}

        <form
          onSubmit={(event) => void handleSubmit(event)}
          className="mt-6 space-y-4"
          noValidate
        >
          <div>
            <label
              htmlFor="login-email"
              className={`mb-1.5 block text-xs font-medium ${
                isDark ? 'text-white/50' : 'text-slate-500'
              }`}
            >
              Email
            </label>
            <div className="relative">
              <Mail className={iconClasses} aria-hidden="true" />
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="operator@dreamwalk.com.au"
                className={inputClasses}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="login-password"
              className={`mb-1.5 block text-xs font-medium ${
                isDark ? 'text-white/50' : 'text-slate-500'
              }`}
            >
              Password
            </label>
            <div className="relative">
              <Lock className={iconClasses} aria-hidden="true" />
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••••••"
                className={inputClasses}
              />
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={submitting}
            leftIcon={<LogIn className="h-4 w-4" aria-hidden="true" />}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </section>
    </main>
  );
}
